import React from 'react';
import './SelectLocationModal.scss';
import { Address } from '../../types/orderTypes';
import analyticsWrapper from '../../utils/analyticsWrapper';
import dayjs from 'dayjs';
import { ButtonComponent } from '../../ReusableComponents/Button/ButtonComponent';
import { InputComponent } from '../../ReusableComponents/Input/InputComponent';

interface State {
  longitude: number;
  latitude: number;
  longitudeInput: string;
  latitudeInput: string;
  searchAddressFound: string | null;
  searchAddressResult: Address | null;
  searchNoResultsFound: boolean;
}

interface Props {
  longitude: number;
  latitude: number;
  targetAddress: string;
  searchName?: string;
  updateLongitude?: (v: number) => void;
  updateLatitude?: (v: number) => void;
  updateSearchAddressResult?: (v: Address | null) => void;
  updateSearchAddressFound?: (v: string) => void;

  recipientPhoneNumber?: string;
  orderReference?: string;
  recipientEmail?: string;
  recipientName?: string;
  orderId?: string;
  date?: string;
  noteForCourier?: string;
}

function getComponent(
  name: string,
  googlePlace: google.maps.places.PlaceResult
): string {
  const component = googlePlace.address_components.find(component => {
    return component.types.includes(name);
  });

  return component ? component.long_name : '';
}

export function convertFromGoogleToOrderAddress(
  googlePlace: google.maps.places.PlaceResult
): Address {
  if (!googlePlace || !googlePlace.address_components) return null;

  return {
    streetName: getComponent('route', googlePlace),
    streetNumber: getComponent('street_number', googlePlace),
    postalCode: getComponent('postal_code', googlePlace),
    city: getComponent('postal_town', googlePlace),
    country: getComponent('country', googlePlace),
  };
}

function makeOpplysningPhoneSearch(recipientPhoneNumber: string) {
  return `https://www.1881.no/?query=${recipientPhoneNumber}`;
}

function makeOplysningenNameSearch(recipientName: string) {
  return `https://www.1881.no/?query=${recipientName}`;
}

export default class SelectLocationContent extends React.Component<
  Props,
  State
> {
  private map: google.maps.Map;
  private mapContainer: HTMLDivElement;
  private searchBox: HTMLInputElement;
  private marker: google.maps.Marker;

  constructor(props: Props) {
    super(props);
    this.state = {
      longitude: this.props.longitude,
      latitude: this.props.latitude,
      longitudeInput: this.props.longitude.toString(),
      latitudeInput: this.props.latitude.toString(),
      searchAddressFound: null,
      searchAddressResult: null,
      searchNoResultsFound: false,
    };
  }

  componentDidMount(): void {
    const lat = this.state.latitude;
    const lng = this.state.longitude;

    const map = new google.maps.Map(this.mapContainer, {
      zoom: 16,
      clickableIcons: true,
      center: { lat: lat, lng: lng },
      disableDefaultUI: false,
      mapTypeControl: false,
      zoomControl: true,
    });
    this.map = map;

    map.addListener('click', e => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      this.props.updateLongitude && this.props.updateLongitude(lng);
      this.props.updateLatitude && this.props.updateLatitude(lat);

      this.setState({
        latitude: lat,
        longitude: lng,
        latitudeInput: lat.toString(),
        longitudeInput: lng.toString(),
        searchAddressFound: null,
        searchAddressResult: null,
      });
      this.placeMarker(lat, lng);
    });

    new google.maps.TrafficLayer().setMap(map);

    // Create the search box and link it to the UI element.
    const input = document.getElementById('pac-input');

    map.controls[google.maps.ControlPosition.TOP_LEFT].push(input);

    const autocomplete = new google.maps.places.Autocomplete(this.searchBox, {
      componentRestrictions: { country: 'no' },
      strictBounds: true,
    });

    let markers = [];

    autocomplete.addListener('place_changed', () => {
      this.setState({
        searchAddressFound: null,
        searchAddressResult: null,
        searchNoResultsFound: false,
      });
      const place = autocomplete.getPlace();

      if (!place) {
        this.setState({
          searchAddressFound: null,
          searchAddressResult: null,
          searchNoResultsFound: true,
        });
        return;
      }

      // Clear out the old markers.
      markers.forEach(marker => {
        marker.setMap(null);
      });
      markers = [];

      const bounds = new google.maps.LatLngBounds();
      let addressFound: string;
      let latFound: number;
      let lngFound: number;
      let searchAddressResult: Address;
      if (place.geometry) {
        // Only if place contains geometry
        if (place.geometry.viewport) {
          // Only geocodes have viewport.
          bounds.union(place.geometry.viewport);
        } else {
          bounds.extend(place.geometry.location);
        }

        if (place.formatted_address) {
          addressFound = place.formatted_address;
          latFound = place.geometry.location.lat();
          lngFound = place.geometry.location.lng();

          searchAddressResult = convertFromGoogleToOrderAddress(place);
        }
      }

      if (addressFound && latFound && lngFound) {
        map.fitBounds(bounds);

        this.placeMarker(latFound, lngFound);

        this.setState({
          searchAddressFound: addressFound,
          searchAddressResult: searchAddressResult,
          searchNoResultsFound: false,
          latitude: latFound,
          longitude: lngFound,
          latitudeInput: latFound.toString(),
          longitudeInput: lngFound.toString(),
        });

        this.props.updateSearchAddressFound &&
          this.props.updateSearchAddressFound(addressFound);
        this.props.updateSearchAddressResult &&
          this.props.updateSearchAddressResult(searchAddressResult);
      } else {
        this.setState({
          searchAddressFound: null,
          searchNoResultsFound: true,
          searchAddressResult: null,
        });

        this.props.updateSearchAddressFound &&
          this.props.updateSearchAddressFound(null);
        this.props.updateSearchAddressResult &&
          this.props.updateSearchAddressResult(null);
      }
    });

    this.placeMarker(lat, lng);
  }

  placeMarker(lat: number, lng: number): void {
    const location = new google.maps.LatLng(lat, lng);
    if (this.marker) {
      this.marker.setPosition(location);
    } else {
      this.marker = new google.maps.Marker({
        position: location,
        map: this.map,
        animation: google.maps.Animation.DROP,
      });
    }
  }

  parseFloat(value: string, defaultValue: number): number {
    try {
      const val = parseFloat(value);
      return Number.isNaN(val) ? defaultValue : val;
    } catch (e) {
      return defaultValue;
    }
  }

  handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      const lat = this.parseFloat(
        this.state.latitudeInput,
        this.state.latitude
      );
      const lng = this.parseFloat(
        this.state.longitudeInput,
        this.state.longitude
      );

      this.placeMarker(lat, lng);
      this.map.setCenter(new google.maps.LatLng(lat, lng));
    }
  };

  updateAddress = (addressChange: Partial<Address>): void => {
    this.setState({
      searchAddressResult: {
        ...this.state.searchAddressResult,
        ...addressChange,
      },
    });
  };

  analyticsEvent = (action: string) => {
    analyticsWrapper.event({
      category: 'address-fix',
      action,
      label: 'click',
    });
  };

  render(): JSX.Element {
    const height = 360;

    return (
      <div className="select-location-modal">
        {this.props.date && (
          <div className="select-location-modal__date">
            {`Date ${dayjs(this.props.date).format('DD.MM')}`}
          </div>
        )}
        <div className="select-location-modal__address">
          <label>Original Address: </label>
          <InputComponent
            id="target-address"
            name="target-address"
            label=""
            variant="outlined"
            margin="dense"
            size="small"
            value={this.props.targetAddress}
            disabled
            fullWidth
          />
        </div>
        <div className="select-location-modal__address">
          <label>Notes:</label>
          <InputComponent
            id="note-for-courier"
            name="note-for-courier"
            label=""
            variant="outlined"
            margin="dense"
            size="small"
            value={this.props.noteForCourier || ''}
            disabled
            fullWidth
          />
        </div>
        <div className="select-location-modal__buttonPosition">
          {this.props.recipientPhoneNumber && (
            <div>
              <p className="text">
                <span role="img" aria-describedby="label">
                  {' '}
                  ☎️{' '}
                </span>{' '}
                {this.props.recipientPhoneNumber}
              </p>
              <ButtonComponent
                variant="outlined"
                onClick={() => {
                  this.analyticsEvent('go-to-1881-phone-search button click');
                  window.open(
                    makeOpplysningPhoneSearch(this.props.recipientPhoneNumber),
                    '_blank'
                  );
                }}
              >
                {'1881 phone 🔍'}
              </ButtonComponent>
            </div>
          )}

          {this.props.recipientName && (
            <div>
              <p className="text">Name: {this.props.recipientName}</p>
              <ButtonComponent
                onClick={() => {
                  this.analyticsEvent('go-to-1881-name-search button click');
                  window.open(
                    makeOplysningenNameSearch(this.props.recipientName),
                    '_blank'
                  );
                }}
                variant="outlined"
              >
                {'1881 name 🔍'}
              </ButtonComponent>
            </div>
          )}
          {this.props.orderId && (
            <div className="link-to-order-section">
              <p className="text">Go to order</p>
              <ButtonComponent
                variant="outlined"
                onClick={() => {
                  this.analyticsEvent('go-to-1881-orderId-search button click');
                }}
              >
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`/orders/${this.props.orderId}`}
                >
                  {`orderId ${this.props.orderId} 🔍`}
                </a>
              </ButtonComponent>
            </div>
          )}
        </div>
        <hr />
        <div className="select-location-modal__input">
          <InputComponent
            id="latitude"
            label="Latitude"
            name="latitude"
            onChange={e => {
              this.setState({ latitudeInput: e.target.value });
            }}
            variant="outlined"
            size="small"
            margin="dense"
            onKeyPress={this.handleKeyPress}
            value={this.state.latitudeInput}
          />
          <InputComponent
            id="longitude"
            label="Longitude"
            name="longitude"
            onChange={e => {
              this.setState({ longitudeInput: e.target.value });
            }}
            variant="outlined"
            size="small"
            margin="dense"
            onKeyPress={this.handleKeyPress}
            value={this.state.longitudeInput}
          />
          <p className="text">
            Order Reference: {this.props.orderReference || 'None'}
          </p>
          <p className="text">Email: {this.props.recipientEmail || 'None'}</p>
        </div>
        {this.state.searchAddressFound && (
          <div className="select-location-modal__searchresult">
            <span>
              <label>Address found in Google Maps: </label>
              <div className="select-location-modal__searchresult-address">
                <div className="input-container">
                  <InputComponent
                    onChange={event =>
                      this.updateAddress({ streetName: event.target.value })
                    }
                    value={this.state.searchAddressResult.streetName}
                    label="Street name"
                    name="street-name"
                    id="street-name"
                    variant="outlined"
                    margin="dense"
                    className="street-input"
                    fullWidth
                  />
                  <InputComponent
                    onChange={event =>
                      this.updateAddress({ streetNumber: event.target.value })
                    }
                    value={this.state.searchAddressResult.streetNumber}
                    name="street-number"
                    id="street-number"
                    margin="dense"
                    label="Street number"
                    variant="outlined"
                    className="street-number-input"
                    fullWidth
                  />
                </div>
                <div className="input-container">
                  <InputComponent
                    onChange={event =>
                      this.updateAddress({ postalCode: event.target.value })
                    }
                    value={this.state.searchAddressResult.postalCode}
                    label="Postal code"
                    name="po-code"
                    id="po-code"
                    variant="outlined"
                    margin="dense"
                    className="po-code-input"
                    fullWidth
                  />
                  <InputComponent
                    onChange={event =>
                      this.updateAddress({ city: event.target.value })
                    }
                    value={this.state.searchAddressResult.city}
                    label="City"
                    name="city"
                    id="city"
                    variant="outlined"
                    margin="dense"
                    className="city-input"
                    fullWidth
                  />
                  <InputComponent
                    onChange={event =>
                      this.updateAddress({ country: event.target.value })
                    }
                    value={this.state.searchAddressResult.country}
                    label="Country"
                    name="country"
                    id="country"
                    variant="outlined"
                    margin="dense"
                    className="country-input"
                    fullWidth
                  />
                </div>
              </div>
            </span>
          </div>
        )}
        {this.state.searchNoResultsFound && (
          <div className="select-location-modal__searchempty">
            <span>
              <label>No search results found for the specified address</label>
            </span>
          </div>
        )}
        <div style={{ height: 10 }} />
        <div className="select-location-modal__content">
          <input
            id="pac-input"
            className="controls"
            type="text"
            placeholder="Type address to search and press enter"
            ref={node => {
              this.searchBox = node;
            }}
            defaultValue={this.props.searchName || this.props.targetAddress}
          />
          <div
            ref={node => {
              this.mapContainer = node;
            }}
            style={{ height, width: '100%' }}
          />
        </div>
      </div>
    );
  }
}
